import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import {
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
} from "@/components/ui/Dropzone";
import { fetchWithAuth } from "@/hooks/useAuth";
import { useCloudflareUpload } from "@/hooks/useCloudflareUpload";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Edit, Image, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { Product } from "./ProductDetailsPage";

function ProductImagePannel({ product }: { product: Product }) {
  const [showCoverDropzone, setShowCoverDropzone] = useState(!product.imageUrl);
  const [showOnPageDropzone, setShowOnPageDropzone] = useState(
    !product.imageUrlOnPage
  );
  const [showGalleryDropzone, setShowGalleryDropzone] = useState(false);
  const queryClient = useQueryClient();

  // COVER uploader (single)
  const coverUpload = useCloudflareUpload({
    maxFiles: 1,
    allowedMimeTypes: ["image/*"],
  });

  // ON PAGE uploader (single)
  const onPageUpload = useCloudflareUpload({
    maxFiles: 1,
    allowedMimeTypes: ["image/*"],
  });

  // GALLERY uploader (multiple)
  const galleryUpload = useCloudflareUpload({
    maxFiles: 10,
    allowedMimeTypes: ["image/*"],
  });

  const updateProductCover = useMutation({
    mutationFn: async (imageUrl: string) => {
      const response = await fetchWithAuth(
        `/api/admin/products/${product.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: product.name,
            description: product.description,
            price: Number(product.price),
            stock: Number(product.stock),
            imageUrl,
            imageUrlOnPage: product.imageUrlOnPage ?? null,
            images: product.images ?? [],
            hide: product.hide,
            pages: product.pages ?? undefined,
            categoryId: product.categoryId ?? undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.message || "Eroare la actualizarea imaginii";
        throw new Error(errorMessage);
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["product", product.id.toString()],
      });
      toast.success("Imagine cover actualizată");
      setShowCoverDropzone(false);
      // Reset state
      coverUpload.setFiles([]);
      coverUpload.setErrors([]);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const updateProductOnPage = useMutation({
    mutationFn: async (imageUrlOnPage: string) => {
      const response = await fetchWithAuth(
        `/api/admin/products/${product.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: product.name,
            description: product.description,
            price: Number(product.price),
            stock: Number(product.stock),
            imageUrl: product.imageUrl ?? null,
            imageUrlOnPage,
            images: product.images ?? [],
            hide: product.hide,
            pages: product.pages ?? undefined,
            categoryId: product.categoryId ?? undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.message || "Eroare la actualizarea imaginii OnPage";
        throw new Error(errorMessage);
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["product", product.id.toString()],
      });
      toast.success("Imagine OnPage actualizată");
      setShowOnPageDropzone(false);
      // Reset state
      onPageUpload.setFiles([]);
      onPageUpload.setErrors([]);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const updateProductGallery = useMutation({
    mutationFn: async (newUrls: string[]) => {
      const merged = Array.from(
        new Set([...(product.images ?? []), ...newUrls])
      );
      const response = await fetchWithAuth(
        `/api/admin/products/${product.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: product.name,
            description: product.description,
            price: Number(product.price),
            stock: Number(product.stock),
            imageUrl: product.imageUrl ?? null,
            imageUrlOnPage: product.imageUrlOnPage ?? null,
            images: merged,
            hide: product.hide,
            pages: product.pages ?? undefined,
            categoryId: product.categoryId ?? undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.message || "Eroare la actualizarea galeriei";
        throw new Error(errorMessage);
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["product", product.id.toString()],
      });
      toast.success("Galerie actualizată");
      setShowGalleryDropzone(false);
      galleryUpload.setFiles([]);
      galleryUpload.setErrors([]);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteGalleryImage = useMutation({
    mutationFn: async (url: string) => {
      // 1) Ask server to delete from Cloudflare
      const del = await fetchWithAuth(`/api/admin/uploads/images/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      if (!del.ok) {
        const err = await del.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la ștergerea imaginii");
      }

      // 2) Update product images field
      const remaining = (product.images ?? []).filter((u) => u !== url);
      const response = await fetchWithAuth(
        `/api/admin/products/${product.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: product.name,
            description: product.description,
            price: Number(product.price),
            stock: Number(product.stock),
            imageUrl: product.imageUrl ?? null,
            imageUrlOnPage: product.imageUrlOnPage ?? null,
            images: remaining,
            hide: product.hide,
            pages: product.pages ?? undefined,
            categoryId: product.categoryId ?? undefined,
          }),
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          err.message || "Eroare actualizare produs după ștergere"
        );
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["product", product.id.toString()],
      });
      toast.success("Imagine ștearsă");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // Cover: after successful upload, update product.imageUrl with the CF public URL
  useEffect(() => {
    if (coverUpload.isSuccess && coverUpload.uploadedResults.length > 0) {
      const first = coverUpload.uploadedResults[0];
      updateProductCover.mutate(first.publicUrl);
    }
  }, [coverUpload.isSuccess, coverUpload.uploadedResults]);

  // OnPage: after successful upload, update product.imageUrlOnPage with the CF public URL
  useEffect(() => {
    if (onPageUpload.isSuccess && onPageUpload.uploadedResults.length > 0) {
      const first = onPageUpload.uploadedResults[0];
      updateProductOnPage.mutate(first.publicUrl);
    }
  }, [onPageUpload.isSuccess, onPageUpload.uploadedResults]);

  // Gallery: after successful upload, append all URLs to product.images
  useEffect(() => {
    if (galleryUpload.isSuccess && galleryUpload.uploadedResults.length > 0) {
      const urls = galleryUpload.uploadedResults.map((r) => r.publicUrl);
      updateProductGallery.mutate(urls);
    }
  }, [galleryUpload.isSuccess, galleryUpload.uploadedResults]);

  const hasGallery = useMemo(
    () => (product.images?.length ?? 0) > 0,
    [product.images]
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Image className="h-5 w-5" />
            Imagini Produs
          </div>
          <div className="flex gap-2">
            {product.imageUrl && !showCoverDropzone && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCoverDropzone(true)}
                className="flex items-center gap-1"
              >
                <Edit className="h-4 w-4" />
                Schimbă cover
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowGalleryDropzone((s) => !s)}
              className="flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              Adaugă imagini
            </Button>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Cover image */}
        <div className="space-y-3">
          <div className="font-medium text-sm text-gray-600">Cover</div>
          {product && !showCoverDropzone && product.imageUrl ? (
            <div className="flex flex-col items-center">
              <div className="relative rounded-md overflow-hidden w-full max-w-md h-64 bg-gray-100">
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          ) : (
            <Dropzone {...coverUpload}>
              <DropzoneEmptyState />
              <DropzoneContent />
            </Dropzone>
          )}
        </div>

        {/* OnPage image */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-medium text-sm text-gray-600">
              Imagine OnPage
            </div>
            {product.imageUrlOnPage && !showOnPageDropzone && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowOnPageDropzone(true)}
                className="flex items-center gap-1"
              >
                <Edit className="h-4 w-4" />
                Schimbă
              </Button>
            )}
          </div>
          {product && !showOnPageDropzone && product.imageUrlOnPage ? (
            <div className="flex flex-col items-center">
              <div className="relative rounded-md overflow-hidden w-full max-w-md h-64 bg-gray-100">
                <img
                  src={product.imageUrlOnPage}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          ) : (
            <Dropzone {...onPageUpload}>
              <DropzoneEmptyState />
              <DropzoneContent />
            </Dropzone>
          )}
        </div>

        {/* Gallery images */}
        <div className="space-y-3">
          <div className="font-medium text-sm text-gray-600">Galerie</div>

          {hasGallery && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(product.images ?? []).map((url) => (
                <div
                  key={url}
                  className="relative group rounded-md overflow-hidden bg-gray-50 border"
                >
                  <img
                    src={url}
                    alt={product.name}
                    className="w-full h-40 object-cover"
                  />
                  <button
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition bg-white rounded p-1 border shadow"
                    onClick={() => deleteGalleryImage.mutate(url)}
                    title="Șterge"
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {showGalleryDropzone && (
            <Dropzone {...galleryUpload}>
              <DropzoneEmptyState />
              <DropzoneContent />
            </Dropzone>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ProductImagePannel;
